import { useEffect, useState } from 'react';

import Card, { CardContent, CardHeader, CardTitle } from '../../../components/ui/Card';
import { usePageTitle } from '../../../hooks/shared';
import { useAuthStore } from '../../identity/store/authStore';

const mermaidDiagram = `graph TD
    subgraph Auth["🔐 AUTHENTICATION & USERS"]
        User["<b>User</b><br/>id, email, passwordHash<br/>firstName, lastName, phone<br/>dateOfBirth, isCitizen<br/>mfaEnabled, role, status"]
        RefreshToken["<b>RefreshToken</b><br/>id, userId, tokenHash<br/>expiresAt, revoked"]
        AuditLog["<b>AuditLog</b><br/>id, userId, action<br/>resourceType, resourceId<br/>metadata, ipAddress"]
        User -->|1:N| RefreshToken
        User -->|1:N| AuditLog
    end

    subgraph Core["🏠 CORE DATA"]
        Address["<b>Address</b><br/>id, userId, addressType<br/>streetAddress, city, state<br/>zipCode, county<br/>congressionalDistrict<br/>stateHouseDistrict<br/>stateSenateDistrict<br/>latitude, longitude"]
        Subscription["<b>Subscription</b><br/>id, userId<br/>stripeCustomerId<br/>tier, status<br/>mailSubscriptionEnabled<br/>mailDeliveryMethod"]
        Payment["<b>Payment</b><br/>id, subscriptionId<br/>stripePaymentIntentId<br/>amount, status<br/>paidAt"]
        User -->|1:N| Address
        User -->|1:1| Subscription
        Subscription -->|1:N| Payment
    end

    subgraph Elections["🗳️ ELECTIONS & BALLOT"]
        Election["<b>Election</b><br/>id, electionDate<br/>electionType, state<br/>name, description<br/>registrationDeadline<br/>earlyVotingStart/End"]
        BallotContest["<b>BallotContest</b><br/>id, electionId<br/>contestType, officeName<br/>district, numberToElect"]
        Candidate["<b>Candidate</b><br/>id, contestId, name<br/>party, photoUrl, bio<br/>websiteUrl, socialMedia<br/>campaignFinanceUrl"]
        BallotMeasure["<b>BallotMeasure</b><br/>id, contestId<br/>measureNumber, title<br/>fullText, summary<br/>fiscalImpact<br/>argumentsFor/Against"]
        Election -->|1:N| BallotContest
        BallotContest -->|1:N| Candidate
        BallotContest -->|1:N| BallotMeasure
    end

    subgraph Polling["📍 POLLING & LOCATIONS"]
        PollingLocation["<b>PollingLocation</b><br/>id, state, county<br/>locationName, address<br/>latitude, longitude<br/>locationType, hours<br/>electionDate"]
        JurisdictionSource["<b>JurisdictionSource</b><br/>id, state, url, domain<br/>notes"]
        ElectionSource["<b>ElectionSource</b><br/>id, electionId, url<br/>domain, isActive<br/>submittedByUserId<br/>submitterFingerprint"]
        Election -->|1:N| ElectionSource
        Election -.->|references| PollingLocation
    end

    subgraph Voter["👤 VOTER INFORMATION"]
        VoterRegistration["<b>VoterRegistration</b><br/>id, userId, addressId<br/>state, county<br/>registrationStatus<br/>partyAffiliation<br/>stateIdNumber"]
        User -->|1:N| VoterRegistration
        Address -.->|optional| VoterRegistration
    end

    subgraph Guides["📋 BALLOT GUIDES"]
        BallotGuide["<b>BallotGuide</b><br/>id, userId, electionId<br/>addressId, generatedAt<br/>contests, pollingLocation<br/>includeSelections<br/>includeSummaries<br/>largePrint"]
        GuideDeliveryRequest["<b>GuideDeliveryRequest</b><br/>id, guideId, userId<br/>deliveryMethod<br/>recipientEmail<br/>mailingAddress<br/>lobLetterId<br/>status, cost"]
        User -->|1:N| BallotGuide
        Election -->|1:N| BallotGuide
        Address -.->|optional| BallotGuide
        BallotGuide -->|1:N| GuideDeliveryRequest
        User -->|1:N| GuideDeliveryRequest
    end

    subgraph Comms["💌 COMMUNICATIONS"]
        EmailLog["<b>EmailLog</b><br/>id, userId, emailType<br/>recipientEmail, subject<br/>sendgridMessageId<br/>status, openedAt<br/>clickedAt, bouncedAt"]
        MailDelivery["<b>MailDelivery</b><br/>id, userId, subscriptionId<br/>addressId, mailType<br/>lobLetterId<br/>deliveryStatus<br/>deliveredAt, cost"]
        Reminder["<b>Reminder</b><br/>id, userId, electionId<br/>reminderType, sendVia<br/>scheduledFor, sentAt<br/>status"]
        User -->|1:N| EmailLog
        User -->|1:N| MailDelivery
        Subscription -.->|optional| MailDelivery
        Address -.->|optional| MailDelivery
        User -->|1:N| Reminder
        Election -->|1:N| Reminder
    end

    subgraph Cache["⚡ CACHING"]
        GeocodingCache["<b>GeocodingCache</b><br/>id, addressHash<br/>rawAddress<br/>latitude, longitude<br/>state, county<br/>congressionalDistrict<br/>expiresAt"]
        DistrictGeometryCache["<b>DistrictGeometryCache</b><br/>id, cacheKey<br/>districtType, stateAbbrev<br/>queryParams, geojson<br/>expiresAt"]
    end

    subgraph DataMgmt["🔄 DATA MANAGEMENT"]
        DataSource["<b>DataSource</b><br/>key, name, type, url<br/>status, lastRunAt<br/>lastRunStatus<br/>lastRunStats"]
        SyncLog["<b>SyncLog</b><br/>id, dataSourceKey<br/>startedAt, finishedAt<br/>status, stats<br/>errorMessage"]
        DataSource -->|1:N| SyncLog
    end

    subgraph Authority["🏛️ ELECTION AUTHORITIES"]
        ElectionAuthority["<b>ElectionAuthority</b><br/>id, jurisdictionType<br/>name, state, county<br/>fipsCode<br/>websiteUrl, calendarUrl<br/>websiteStatus<br/>scraperStatus<br/>scraperConfig"]
        WebsiteCheck["<b>WebsiteCheck</b><br/>id, authorityId<br/>checkedAt, httpStatus<br/>contentHash<br/>changeDetected<br/>errorMessage"]
        AuthoritySubmission["<b>AuthoritySubmission</b><br/>id, authorityId<br/>jurisdictionType, name<br/>state, county<br/>submittedUrl<br/>submittedByUserId<br/>status, reviewedByUserId"]
        ElectionAuthority -->|1:N| WebsiteCheck
        ElectionAuthority -->|1:N| AuthoritySubmission
        User -->|1:N| AuthoritySubmission
    end

    subgraph Discovery["🔍 DISCOVERY"]
        DiscoverySession["<b>DiscoverySession</b><br/>id, state, status<br/>startedAt, completedAt<br/>pagesVisited<br/>crawledUrls<br/>topPages, patterns"]
    end

    subgraph Analytics["📊 ANALYTICS"]
        PageView["<b>PageView</b><br/>id, path, views<br/>updatedAt"]
    end

    style Auth fill:#e3f2fd
    style Core fill:#f3e5f5
    style Elections fill:#e8f5e9
    style Polling fill:#fff3e0
    style Voter fill:#fce4ec
    style Guides fill:#f1f8e9
    style Comms fill:#e0f2f1
    style Cache fill:#ede7f6
    style DataMgmt fill:#fff9c4
    style Authority fill:#ffe0b2
    style Discovery fill:#f8bbd0
    style Analytics fill:#e1f5fe
`;

export default function AdminDataModel() {
  const user = useAuthStore((s) => s.user);
  const [mermaidLoaded, setMermaidLoaded] = useState(false);

  usePageTitle('Data Model');

  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50 px-4">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <p className="text-red-600 font-medium">Access denied</p>
            <p className="text-base text-neutral-600 mt-2">Admin access required</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  useEffect(() => {
    // Load mermaid from CDN
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js';
    script.async = true;
    script.onload = () => {
      window.mermaid.initialize({ startOnLoad: true, theme: 'default' });
      window.mermaid.contentLoaded();
      setMermaidLoaded(true);
    };
    document.body.appendChild(script);
    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-neutral-900 mb-2">Data Model</h1>
          <p className="text-neutral-600">
            Database schema with all tables, relationships, and key fields
          </p>
        </div>

        {/* Legend */}
        <Card patrioticShadow className="mb-8">
          <CardHeader>
            <CardTitle>Data Model Categories</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-blue-100 border border-blue-300" />
                <span className="text-base text-neutral-700">Authentication</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-purple-100 border border-purple-300" />
                <span className="text-base text-neutral-700">Core Data</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-green-100 border border-green-300" />
                <span className="text-base text-neutral-700">Elections</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-amber-100 border border-amber-300" />
                <span className="text-base text-neutral-700">Polling</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-pink-100 border border-pink-300" />
                <span className="text-base text-neutral-700">Voter Info</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-lime-100 border border-lime-300" />
                <span className="text-base text-neutral-700">Guides</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-teal-100 border border-teal-300" />
                <span className="text-base text-neutral-700">Communications</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-indigo-100 border border-indigo-300" />
                <span className="text-base text-neutral-700">Caching</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-300" />
                <span className="text-base text-neutral-700">Data Management</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-orange-100 border border-orange-300" />
                <span className="text-base text-neutral-700">Authorities</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-rose-100 border border-rose-300" />
                <span className="text-base text-neutral-700">Discovery</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 rounded bg-cyan-100 border border-cyan-300" />
                <span className="text-base text-neutral-700">Analytics</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ER Diagram */}
        <Card patrioticShadow className="overflow-hidden">
          <CardContent className="p-6">
            {!mermaidLoaded && (
              <div className="flex items-center justify-center h-96 bg-neutral-100 rounded-lg">
                <p className="text-neutral-500">Loading diagram...</p>
              </div>
            )}
            {mermaidLoaded && <div className="mermaid overflow-x-auto">{mermaidDiagram}</div>}
          </CardContent>
        </Card>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          <Card>
            <CardContent className="p-4">
              <p className="text-base text-neutral-600">Total Tables</p>
              <p className="text-2xl font-bold text-neutral-900">28</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-base text-neutral-600">Core Tables</p>
              <p className="text-2xl font-bold text-neutral-900">4</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-base text-neutral-600">Relationships</p>
              <p className="text-2xl font-bold text-neutral-900">35+</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <p className="text-base text-neutral-600">Data Categories</p>
              <p className="text-2xl font-bold text-neutral-900">12</p>
            </CardContent>
          </Card>
        </div>

        {/* Table Reference */}
        <Card patrioticShadow className="mt-8">
          <CardHeader>
            <CardTitle>Table Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="font-semibold text-blue-900">Authentication (3)</p>
                <p className="text-base text-blue-700 mt-1">User, RefreshToken, AuditLog</p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                <p className="font-semibold text-purple-900">Core Data (4)</p>
                <p className="text-base text-purple-700 mt-1">
                  Address, Subscription, Payment, VoterRegistration
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                <p className="font-semibold text-green-900">Elections (4)</p>
                <p className="text-base text-green-700 mt-1">
                  Election, BallotContest, Candidate, BallotMeasure
                </p>
              </div>
              <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                <p className="font-semibold text-amber-900">Polling (3)</p>
                <p className="text-base text-amber-700 mt-1">
                  PollingLocation, JurisdictionSource, ElectionSource
                </p>
              </div>
              <div className="p-4 bg-teal-50 rounded-lg border border-teal-200">
                <p className="font-semibold text-teal-900">Communications (4)</p>
                <p className="text-base text-teal-700 mt-1">
                  EmailLog, MailDelivery, Reminder, BallotGuide
                </p>
              </div>
              <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <p className="font-semibold text-indigo-900">Caching (2)</p>
                <p className="text-base text-indigo-700 mt-1">
                  GeocodingCache, DistrictGeometryCache
                </p>
              </div>
              <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                <p className="font-semibold text-yellow-900">Data Management (2)</p>
                <p className="text-base text-yellow-700 mt-1">DataSource, SyncLog</p>
              </div>
              <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                <p className="font-semibold text-orange-900">Authorities (4)</p>
                <p className="text-base text-orange-700 mt-1">
                  ElectionAuthority, WebsiteCheck, AuthoritySubmission, DiscoverySession
                </p>
              </div>
              <div className="p-4 bg-cyan-50 rounded-lg border border-cyan-200">
                <p className="font-semibold text-cyan-900">Analytics (1)</p>
                <p className="text-base text-cyan-700 mt-1">PageView</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
